<template>
  <el-dialog
    :visible.sync="dialogVisible"
    title="Analysis Results"
    width="95%"
    :before-close="handleClose"
    class="results-modal"
    top="5vh"
  >
    <div v-if="results" class="results-container">
      <!-- AI Audio Analysis Section -->
      <div v-if="results.ai" class="ai-section">
        <div class="section-header">
          <i class="el-icon-headset"></i>
          <h3>Audio Track Analysis</h3>
        </div>
        <div class="ai-content">
          <div class="ai-card">
            <div class="ai-label">AI Generated Detection</div>
            <div class="ai-value">
              <el-tag
                :type="results.ai.is_ai_generated ? 'warning' : 'success'"
                size="large"
              >
                {{ results.ai.is_ai_generated ? 'AI Generated' : 'Human Generated' }}
              </el-tag>
              <span class="confidence">
                Confidence: {{ results.ai.confidence }}%
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Video Analysis Section -->
      <div v-if="results.deep && results.deep.length > 0" class="videos-section">
        <div class="section-header">
          <i class="el-icon-video-camera"></i>
          <h3>Video Analysis ({{ results.deep.length }} video{{ results.deep.length > 1 ? 's' : '' }})</h3>
        </div>

        <div
          v-for="(video, index) in results.deep"
          :key="index"
          class="video-card"
        >
          <div class="video-card-header">
            <h4>
              <i class="el-icon-film"></i>
              Video {{ index + 1 }}
            </h4>
            <el-tag
              :type="video.found ? 'success' : 'info'"
              size="medium"
            >
              {{ video.found ? 'Track Found' : 'Track Not Found' }}
            </el-tag>
          </div>

          <div class="video-card-body">
            <!-- Basic Video Info -->
            <div class="info-grid">
              <div class="info-item">
                <span class="info-label">
                  <i class="el-icon-time"></i>
                  Video Duration
                </span>
                <span class="info-value">{{ formatDuration(video.video_duration) }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">
                  <i class="el-icon-headset"></i>
                  Audio Track Duration
                </span>
                <span class="info-value">{{ formatDuration(video.audio_track_duration) }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">
                  <i class="el-icon-search"></i>
                  Detected Duration
                </span>
                <span class="info-value">{{ formatDuration(video.detected_duration) }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">
                  <i class="el-icon-data-line"></i>
                  Similarity
                </span>
                <span class="info-value">{{ video.similarity }}%</span>
              </div>
              <div v-if="video.volume !== undefined" class="info-item">
                <span class="info-label">
                  <i class="el-icon-bell"></i>
                  Volume
                </span>
                <span class="info-value">{{ video.volume }}%</span>
              </div>
              <div class="info-item">
                <span class="info-label">
                  <i class="el-icon-picture"></i>
                  AI Generated
                </span>
                <el-tag :type="video.ai_generated ? 'warning' : 'success'" size="small">
                  {{ video.ai_generated ? 'Yes' : 'No' }}
                </el-tag>
                <span class="confidence-small">({{ video.ai_confidence }}%)</span>
              </div>
            </div>

            <!-- Vocals Analysis -->
            <div v-if="video.vocals_analysis" class="vocals-section">
              <div class="subsection-header">
                <i class="el-icon-microphone"></i>
                <h5>Vocal & Spoken Content Analysis</h5>
              </div>

              <div class="vocals-content">
                <!-- Language -->
                <div class="vocals-item">
                  <span class="vocals-label">
                    <i class="el-icon-chat-dot-round"></i>
                    Language
                  </span>
                  <el-tag size="small">{{ video.vocals_analysis.language.toUpperCase() }}</el-tag>
                </div>

                <!-- Transcript -->
                <div v-if="video.vocals_analysis.transcript" class="transcript-container">
                  <div class="transcript-header">
                    <i class="el-icon-document"></i>
                    <strong>Transcript:</strong>
                  </div>
                  <div class="transcript-text">
                    <p v-if="!expandedTranscripts[index]">
                      {{ truncateText(video.vocals_analysis.transcript, 200) }}
                      <el-button
                        v-if="video.vocals_analysis.transcript.length > 200"
                        type="text"
                        size="small"
                        @click="toggleTranscript(index)"
                      >
                        Show More
                      </el-button>
                    </p>
                    <p v-else>
                      {{ video.vocals_analysis.transcript }}
                      <el-button
                        type="text"
                        size="small"
                        @click="toggleTranscript(index)"
                      >
                        Show Less
                      </el-button>
                    </p>
                  </div>
                </div>

                <!-- Sentiment Analysis -->
                <div v-if="video.vocals_analysis.sentiment" class="sentiment-container">
                  <div class="sentiment-header">
                    <i class="el-icon-data-analysis"></i>
                    <strong>Sentiment Analysis:</strong>
                  </div>
                  <div class="sentiment-data">
                    <div class="sentiment-item">
                      <span>Label:</span>
                      <el-tag
                        :type="getSentimentType(video.vocals_analysis.sentiment.label)"
                        size="small"
                      >
                        {{ video.vocals_analysis.sentiment.label }}
                      </el-tag>
                    </div>
                  </div>
                </div>

                <!-- Topic Analysis -->
                <div v-if="video.vocals_analysis.topic" class="topic-container">
                  <div class="topic-header">
                    <i class="el-icon-collection-tag"></i>
                    <strong>Topic Classification:</strong>
                  </div>
                  <div class="topic-data">
                    <div class="topic-item">
                      <span>Category:</span>
                      <el-tag type="primary" size="small">
                        {{ video.vocals_analysis.topic.category }}
                      </el-tag>
                      <span class="confidence-small">({{ (video.vocals_analysis.topic.confidence * 100).toFixed(1) }}%)</span>
                    </div>
                    <div v-if="video.vocals_analysis.topic.secondary_categories && video.vocals_analysis.topic.secondary_categories.length > 0" class="topic-item">
                      <span>Secondary Categories:</span>
                      <div class="secondary-tags">
                        <el-tag
                          v-for="(secondary, secIndex) in video.vocals_analysis.topic.secondary_categories"
                          :key="secIndex"
                          type="info"
                          size="mini"
                        >
                          {{ secondary.category }} ({{ (secondary.confidence * 100).toFixed(1) }}%)
                        </el-tag>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Offensive Content -->
                <div class="offensive-container">
                  <div class="offensive-header">
                    <i class="el-icon-warning"></i>
                    <strong>Content Moderation:</strong>
                  </div>
                  <div class="offensive-data">
                    <div class="offensive-item">
                      <span>Offensive Content:</span>
                      <el-tag
                        :type="video.vocals_analysis.offensive_content ? 'danger' : 'success'"
                        size="small"
                      >
                        {{ video.vocals_analysis.offensive_content ? 'Detected' : 'None' }}
                      </el-tag>
                    </div>
                    <div v-if="video.vocals_analysis.offensive_words && video.vocals_analysis.offensive_words.length > 0" class="offensive-item">
                      <span>Offensive Words:</span>
                      <div class="offensive-tags">
                        <el-tag
                          v-for="(word, wordIndex) in video.vocals_analysis.offensive_words"
                          :key="wordIndex"
                          type="danger"
                          size="mini"
                        >
                          {{ word }}
                        </el-tag>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <span slot="footer" class="dialog-footer">
      <el-button type="primary" @click="handleClose">Close</el-button>
    </span>
  </el-dialog>
</template>

<script>
export default {
  name: 'AnalysisResultsModal',

  props: {
    visible: {
      type: Boolean,
      default: false
    },
    results: {
      type: Object,
      default: null
    }
  },

  data: function() {
    return {
      expandedTranscripts: {}
    }
  },

  computed: {
    dialogVisible: {
      get: function() {
        return this.visible
      },
      set: function(value) {
        if (!value) {
          this.$emit('update:visible', false)
        }
      }
    }
  },

  watch: {
    visible: function(newVal) {
      if (newVal) {
        this.expandedTranscripts = {}
      }
    }
  },

  methods: {
    handleClose: function() {
      this.$emit('update:visible', false)
    },

    truncateText: function(text, maxLength) {
      if (!text || text.length <= maxLength) return text
      return text.substring(0, maxLength) + '...'
    },

    toggleTranscript: function(index) {
      this.$set(this.expandedTranscripts, index, !this.expandedTranscripts[index])
    },

    getSentimentType: function(sentiment) {
      var sentimentMap = {
        'positive': 'success',
        'negative': 'danger',
        'neutral': 'info'
      }
      return sentimentMap[sentiment.toLowerCase()] || 'info'
    },

    formatDuration: function(seconds) {
      if (seconds === null || seconds === undefined) return '00:00'

      var roundedSeconds = Math.round(seconds)
      var minutes = Math.floor(roundedSeconds / 60)
      var remainingSeconds = roundedSeconds % 60

      var minutesStr = String(minutes).padStart(2, '0')
      var secondsStr = String(remainingSeconds).padStart(2, '0')

      return minutesStr + ':' + secondsStr
    }
  }
}
</script>

<style lang="scss" scoped>
.results-modal {
  ::v-deep .el-dialog {
    border-radius: 12px;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
  }

  ::v-deep .el-dialog__header {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    padding: 20px;
    border-radius: 12px 12px 0 0;

    .el-dialog__title {
      color: white;
      font-size: 20px;
      font-weight: 600;
    }

    .el-dialog__headerbtn .el-dialog__close {
      color: white;
      font-size: 20px;

      &:hover {
        color: rgba(255, 255, 255, 0.8);
      }
    }
  }

  ::v-deep .el-dialog__body {
    padding: 20px;
    overflow-y: auto;
    max-height: calc(90vh - 180px);
  }

  ::v-deep .el-dialog__footer {
    padding: 15px 20px;
    border-top: 1px solid #e9ecef;
  }
}

.results-container {
  display: flex;
  flex-direction: column;
  gap: 25px;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 2px solid #e9ecef;

  i {
    font-size: 24px;
    color: #667eea;
  }

  h3 {
    margin: 0;
    font-size: 20px;
    color: #495057;
    font-weight: 600;
  }
}

.ai-section {
  .ai-content {
    display: flex;
    justify-content: center;
  }

  .ai-card {
    background: linear-gradient(135deg, #f6f9fc 0%, #ffffff 100%);
    border: 2px solid #e1e8ed;
    border-radius: 12px;
    padding: 25px;
    text-align: center;
    min-width: 400px;

    .ai-label {
      font-size: 14px;
      color: #868e96;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      margin-bottom: 15px;
    }

    .ai-value {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 10px;

      .el-tag {
        font-size: 16px;
        font-weight: 600;
      }

      .confidence {
        font-size: 13px;
        color: #606266;
      }
    }
  }
}

.videos-section {
  .video-card {
    background: white;
    border: 2px solid #e9ecef;
    border-radius: 12px;
    margin-bottom: 20px;
    overflow: hidden;
    transition: all 0.3s ease;

    &:hover {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    }

    &:last-child {
      margin-bottom: 0;
    }

    .video-card-header {
      background: linear-gradient(135deg, #f6f9fc 0%, #ffffff 100%);
      padding: 15px 20px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      border-bottom: 1px solid #e9ecef;

      h4 {
        margin: 0;
        font-size: 16px;
        color: #495057;
        display: flex;
        align-items: center;
        gap: 8px;

        i {
          color: #667eea;
        }
      }
    }

    .video-card-body {
      padding: 20px;
    }
  }
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
  margin-bottom: 20px;

  .info-item {
    background: #f8f9fa;
    padding: 12px 15px;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    gap: 8px;

    .info-label {
      font-size: 12px;
      color: #868e96;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      display: flex;
      align-items: center;
      gap: 6px;

      i {
        color: #4facfe;
      }
    }

    .info-value {
      font-size: 15px;
      color: #495057;
      font-weight: 600;
    }

    .confidence-small {
      font-size: 11px;
      color: #868e96;
      margin-left: 5px;
    }
  }
}

.vocals-section {
  .subsection-header {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 15px;
    padding-bottom: 8px;
    border-bottom: 1px solid #e9ecef;

    i {
      font-size: 18px;
      color: #667eea;
    }

    h5 {
      margin: 0;
      font-size: 16px;
      color: #495057;
      font-weight: 600;
    }
  }

  .vocals-content {
    display: flex;
    flex-direction: column;
    gap: 15px;
  }

  .vocals-item {
    display: flex;
    align-items: center;
    gap: 10px;

    .vocals-label {
      font-size: 13px;
      color: #606266;
      font-weight: 500;
      display: flex;
      align-items: center;
      gap: 6px;

      i {
        color: #4facfe;
      }
    }
  }
}

.transcript-container,
.sentiment-container,
.topic-container,
.offensive-container {
  background: #f8f9fa;
  padding: 15px;
  border-radius: 8px;
  border-left: 3px solid #667eea;

  .transcript-header,
  .sentiment-header,
  .topic-header,
  .offensive-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 10px;
    color: #495057;
    font-size: 14px;

    i {
      color: #667eea;
      font-size: 16px;
    }
  }

  .transcript-text {
    p {
      margin: 0;
      line-height: 1.6;
      color: #495057;
      font-size: 13px;
    }

    .el-button--text {
      margin-left: 8px;
      padding: 0;
      font-weight: 600;
      color: #667eea;

      &:hover {
        color: #764ba2;
      }
    }
  }
}

.sentiment-data,
.topic-data,
.offensive-data {
  display: flex;
  flex-direction: column;
  gap: 10px;

  .sentiment-item,
  .topic-item,
  .offensive-item {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 13px;

    > span:first-child {
      color: #606266;
      font-weight: 500;
      min-width: 100px;
    }

    .sentiment-value,
    .topic-value {
      color: #495057;
      font-weight: 600;
    }

    .confidence-small {
      font-size: 11px;
      color: #868e96;
      margin-left: 5px;
    }
  }

  .secondary-tags,
  .offensive-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
}
</style>
